import { useCallback, useEffect, useState } from "react";
import type { ReactElement } from "react";
import type {
  AircallStatus, ApiClient, DatevKopplung, Dienst, SchnittstellenStand, Unternehmen,
} from "@kundeninfo/api-client";
import { AircallDienst, AIRCALL_NAME, aircallZustand } from "./AircallDienst.js";
import type { AircallClient } from "./AircallDienst.js";
import { DatevBereich, DATEV_NAME, DATEV_SCHLUESSEL } from "./DatevKopplung.js";
import type { DatevClient } from "./DatevKopplung.js";
import { Chip, Hinweis, Menueeintrag } from "@kundeninfo/ui/me-design";
import { formatZeitpunkt } from "../format.js";
import { KiKnopf, Navigationsleiste } from "../components/meHuellen.js";
import "./Schnittstellen.css";

/**
 * Der Zustand der angebundenen Fremddienste — der Hauptnavigationspunkt
 * „Schnittstellen", offen für JEDEN angemeldeten Bearbeiter.
 *
 * Die Ansicht ist bewusst arm: Gemessen wird auf dem SERVER (`GET
 * /api/schnittstellen/status`, siehe packages/api/src/routes/schnittstellen.ts), hier wird
 * nur gezeigt. Das ist keine Bequemlichkeit, sondern die Sicherheitsgrenze — Adresse und
 * Zugriffskopf eines Dienstes stehen nirgends in der Antwort und dürfen es auch nie: Sonst
 * läge das Zugangsgeheimnis der Render-Engine im Browser jedes Bearbeiters.
 *
 * Sie ist von Anfang an eine LISTE, obwohl heute nur die Render-Engine darin steht. DATEV
 * und der Portal-Abgleich kommen mit ihren Adaptern dazu; eine Einzelkachel müsste dafür
 * erst wieder auseinandergenommen werden.
 *
 * **Seit Etappe 3 der Bereichs-Spalten steht diese Liste in der SPALTE.** Bis dahin war
 * „Schnittstellen" — neben den Einstellungen — der letzte Bereich, der ohne Spalte in voller
 * Breite stand und damit aus der Reihe fiel. Jetzt gilt hier dasselbe Muster wie überall:
 * links die Dienste, rechts der gewählte. Es ist ein reiner ANGLEICH — es kommt kein Dienst
 * hinzu, keine Auskunft verschwindet, und gemessen wird unverändert bei jedem Öffnen einmal
 * und danach auf Knopfdruck.
 *
 * **Warum ein Hook und keine Komponente** (Muster der Bereiche aus Etappe 1): Die Ansicht
 * füllt seither ZWEI Bereiche des Rahmens (`MeSchale.list` und `MeSchale.detail`), die sich
 * EINEN Stand teilen — die eine Messung trägt beide Seiten. Eine Komponente kann ihre
 * Ausgabe nicht auf zwei Eigenschaften des Rahmens verteilen; zwei Komponenten müssten den
 * Stand irgendwo darüber halten, und „irgendwo darüber" wäre App.tsx.
 *
 * `stand` ist der Zeitpunkt DIESER Messung, nicht der eines Zwischenspeichers — der
 * Aktualisieren-Knopf ruft schlicht noch einmal an. Deshalb steht die Uhrzeit dabei
 * (formatZeitpunkt): Ohne sie liesse sich nicht sehen, ob der Knopf gewirkt hat. Sie steht
 * im SPALTENKOPF neben dem Knopf und nicht im Detail: Gemessen wird die ganze Liste, nicht
 * der eine geöffnete Dienst.
 *
 * Der CSS-Block heisst `schn` und gehört ALLEIN dieser Ansicht — kein geteilter Blockname
 * über Ansichten hinweg (Lektion aus `.ka__`). Er trägt Spalte UND Detail, weil beide aus
 * dieser einen Datei kommen; die Optik der Spalteneinträge folgt dem Untermenü der
 * Verwaltung, ihre Regeln stehen aber eigens in Schnittstellen.css.
 *
 * **Der AIRCALL-Dienst (Spec vom 07.09.2026, Entscheid vom 11.09.2026) ist die erste
 * Zeile, die NICHT aus `/api/schnittstellen/status` kommt.** Er hat einen eigenen Weg
 * (`/api/aircall/status`), weil seine Auskunft eine andere Gestalt hat als die einer
 * Dienstzeile: Rückstand in Tagen, Anrufe gesamt, Anrufe ungeklärt. Diese Felder in das
 * Dienste-Schema zu pressen hiesse, jedem künftigen Dienst dieselben Felder mitzugeben
 * (leer), und das gemeinsame Schema wäre nach dem zweiten Sonderfall keines mehr.
 *
 * Die ZEILE ist deshalb hier zusammengesetzt und die beiden Messungen laufen nebeneinander
 * (`Promise.allSettled`): Scheitert die eine, steht die andere trotzdem da. Ein Fehlschlag
 * der Dienste-Messung darf die Aircall-Zeile nicht mitnehmen — und umgekehrt.
 */

/** Die Wege, die diese Ansicht braucht — als `Pick`, damit der Test sie mit ein paar
 *  `vi.fn` zeichnen kann (Muster: BereichArtikelClient). */
export type SchnittstellenClient =
  Pick<ApiClient, "interfaceStatus" | "aircallStatus" | "listDatevKopplungen">
  & AircallClient & DatevClient;

export type SchnittstellenProps = {
  /** Steht der Bereich gerade auf dem Bildschirm? Der Hook wird — Regeln der Hooks — auch
   *  an anderen Adressen gerufen; ohne diesen Riegel misse er überall mit. */
  aktiv: boolean;
  client: SchnittstellenClient;
  /**
   * Der geöffnete Dienst aus der Adresse (`/schnittstellen/:dienst`) — `null` heisst
   * „keiner". Der Wert ist die KLEINGESCHRIEBENE Kennung des Dienstes; siehe
   * `dienstPfad` unten.
   */
  dienstSchluessel: string | null;
  /** Adresswechsel innerhalb des Bereichs. Der Bereich navigiert NICHT selbst — die
   *  Adresse kennt App.tsx (dasselbe Muster wie in den Bereichen der Etappe 1). */
  onOeffnen: (dienstSchluessel: string | null) => void;
  /** Für die Kürzel-Chips der Kundensuche im Aircall-Panel. Ohne Angabe bleiben sie
   *  neutral — der Rest der Ansicht braucht sie nicht. */
  companies?: Unternehmen[];
};

export type SchnittstellenTeile = {
  /** Für `MeSchale.list`. */
  spalte: ReactElement;
  /** Für `MeSchale.detail`. */
  detail: ReactElement;
};

/** Die deutsche Beschriftung eines Zustands. Vollständige Zuordnung statt eines
 *  Rückfalls: Käme ein vierter Zustand hinzu, meldete das der Typprüfer, statt eine rohe
 *  Konstante wie „NICHT_ERREICHBAR" auf den Bildschirm zu lassen. */
const ZUSTAND_LABEL: Record<Dienst["zustand"], string> = {
  ERREICHBAR: "Erreichbar",
  NICHT_ERREICHBAR: "Nicht erreichbar",
  // „Bereit" und nicht „Erreichbar": Bei diesem Dienst geht nichts hinaus, er wird
  // ABGEHOLT (der Monitor-Weg seit dem 07.09.2026). Es wurde niemand gefragt — ein Wort,
  // das eine Messung behauptet, die es nicht gab, wäre auf einer Statusseite das
  // Schlimmste.
  BEREIT: "Bereit",
  NICHT_KONFIGURIERT: "Nicht konfiguriert",
};

/**
 * Die Ampelfarbe je Zustand — als Klassen-Endung, nie als Farbwert hier im Code: Die
 * Farben selbst stehen in den Tokens (packages/ui/src/kundeninfo.css) und wechseln mit dem
 * Farbschema mit.
 *
 * „Nicht konfiguriert" ist bewusst GRAU und nicht rot: Ein Dienst, den niemand hinterlegt
 * hat, ist nicht kaputt — nur nicht angebunden. Rot hiesse „hier ist etwas passiert" und
 * schickte jemanden auf die Suche nach einem Fehler, den es nicht gibt.
 */
/** Ampelklasse → Status-Art des Zustands-Chips (me-chip `status-art`, P12 W2). */
function statusArt(klasse: string): "ok" | "fehler" | "warnung" | "laeuft" | "aus" {
  switch (klasse) {
    case "ok": return "ok";
    case "schlecht": return "fehler";
    case "warn": return "warnung";
    case "laeuft": return "laeuft";
    default: return "aus";
  }
}

const ZUSTAND_KLASSE: Record<Dienst["zustand"], string> = {
  ERREICHBAR: "ok",
  NICHT_ERREICHBAR: "schlecht",
  // Dieselbe Farbe wie ERREICHBAR, und das ist richtig: Für den Leser sagen beide „hier
  // ist alles, wie es sein soll". Die Ampel unterscheidet Zustände nach ihrer FOLGE, nicht
  // nach ihrer Herkunft; den Unterschied trägt das Wort daneben.
  BEREIT: "ok",
  NICHT_KONFIGURIERT: "aus",
};

/**
 * Die DATEV-Zeile (Teilprojekt 5a). Wie die Aircall-Zeile kommt sie NICHT aus
 * `/api/schnittstellen/status`: Ihre Auskunft ist eine andere — „für wie viele
 * Unternehmen ist die Kopplung eingeschaltet?" —, und dieses Feld in das gemeinsame Schema
 * zu pressen hiesse, jedem künftigen Dienst ein leeres Feld mitzugeben.
 *
 * Der Zustand ist BEREIT, sobald mindestens eine Kopplung aktiv ist, sonst NICHT
 * KONFIGURIERT — und nie „erreichbar": In Stufe 1 geht nichts hinaus, es wird niemand
 * gefragt (dieselbe Ehrlichkeit wie beim Monitor-Abgleich). Ein Fehlschlag der Messung
 * (auch eine 403 für Nicht-ADMIN) heisst „Nicht abfragbar" — dasselbe Muster wie beim
 * Aircall-Dienst: Über den EINRICHTUNGSSTAND ist damit nichts gesagt.
 *
 * Sie erscheint NUR für ADMIN (Ruling D23) — der Server antwortet anderen mit 403 auf
 * `listDatevKopplungen`, und die Zeile fällt dann auf „Nicht abfragbar" statt auf eine
 * Zeile, die für jeden anderen ins Leere führt.
 */
function datevZustand(
  kopplungen: DatevKopplung[] | null, fehler: boolean,
): { label: string; klasse: string } {
  if (fehler) return { label: "Nicht abfragbar", klasse: "warn" };
  if (kopplungen === null) return { label: "Wird gemessen", klasse: "aus" };
  return kopplungen.some((k) => k.active)
    ? { label: "Bereit", klasse: "ok" }
    : { label: "Nicht konfiguriert", klasse: "aus" };
}

/**
 * Der Adress-Anteil eines Dienstes: seine Kennung, kleingeschrieben.
 *
 * Die Dienste HABEN stabile Kennungen — `schluessel` steht im Vertrag der Route und wird
 * dort als Konstante vergeben (`RENDER_ENGINE`, siehe api/src/routes/schnittstellen.ts,
 * wo ausdrücklich steht: „die Maske hängt ihr Symbol daran, nicht am Namen"). Ein aus dem
 * Anzeigenamen gebildeter Behelfs-Slug ist deshalb nicht nötig: Ein Lesezeichen auf
 * `/schnittstellen/render_engine` überlebt eine Umbenennung der Zeile.
 *
 * Kleingeschrieben und sonst UNVERÄNDERT: Adressen dieser Anwendung sind durchweg klein
 * (`/verwaltung/zahlungskonditionen`), und das Kleinschreiben ist die einzige Umformung,
 * die für JEDE denkbare Kennung umkehrbar bleibt. Ein Ersetzen von `_` durch `-` sähe
 * hübscher aus, liesse aber zwei Kennungen, die sich nur darin unterscheiden, auf dieselbe
 * Adresse fallen.
 */
function dienstPfad(schluessel: string): string {
  return schluessel.toLowerCase();
}

/**
 * Die Kennung des Aircall-Dienstes — sie kommt NICHT vom Server (siehe der Kopf dieser
 * Datei) und steht deshalb hier als Konstante. Die Adresse dazu ist `/schnittstellen/aircall`;
 * ein Lesezeichen darauf überlebt jede Umbenennung der Zeile, wie bei den übrigen Diensten.
 */
const AIRCALL_SCHLUESSEL = "AIRCALL";
const AIRCALL_PFAD = dienstPfad(AIRCALL_SCHLUESSEL);

/** Die Kennung des DATEV-Dienstes (Teilprojekt 5a) — wie bei Aircall kommt sie NICHT vom
 *  Server. `DATEV_SCHLUESSEL` ist bereits kleingeschrieben ("datev"), `dienstPfad` bleibt
 *  trotzdem angewandt: derselbe Weg wie bei jeder anderen Kennung dieser Datei. */
const DATEV_PFAD = dienstPfad(DATEV_SCHLUESSEL);

/** Eine Zeile der Dienste-Spalte — die gemeinsame Gestalt für die gemessenen Dienste des
 *  Servers UND den hier zusammengesetzten Aircall-Eintrag. */
type SpaltenZeile = {
  schluessel: string;
  pfad: string;
  name: string;
  label: string;
  klasse: string;
};

export function useSchnittstellen(p: SchnittstellenProps): SchnittstellenTeile {
  const { aktiv, dienstSchluessel } = p;

  const [stand, setStand] = useState<SchnittstellenStand | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [misst, setMisst] = useState(false);
  const [aircall, setAircall] = useState<AircallStatus | null>(null);
  const [aircallFehler, setAircallFehler] = useState(false);
  /** Die Kopplungen ALLER Unternehmen — nur um zu wissen, ob MINDESTENS eine aktiv ist
   *  (die Ampel der Zeile). Das gewählte Unternehmen für das DATEV-Detail hält ein
   *  eigener Zustand weiter unten. */
  const [datevKopplungen, setDatevKopplungen] = useState<DatevKopplung[] | null>(null);
  const [datevFehler, setDatevFehler] = useState(false);
  /** Das im DATEV-Detail gewählte Unternehmen — eigener Zustand, weil `Schnittstellen.tsx`
   *  ihn hält (siehe der Kopfkommentar von DatevKopplung.tsx: „das gewählte Unternehmen
   *  hält Schnittstellen.tsx im eigenen Zustand"). */
  const [datevCompanyId, setDatevCompanyId] = useState<string | null>(null);

  const interfaceStatus = p.client.interfaceStatus;
  const aircallStatus = p.client.aircallStatus;
  const listDatevKopplungen = p.client.listDatevKopplungen;

  const messen = useCallback((): void => {
    setMisst(true);
    // NEBENEINANDER und mit `allSettled`: Drei Wege, drei Auskünfte. Mit `Promise.all`
    // nähme der erste Fehlschlag die anderen Zeilen mit — und die Statusseite verschwiege
    // ausgerechnet dann etwas, wenn gerade etwas nicht stimmt.
    void Promise.allSettled([interfaceStatus(), aircallStatus(), listDatevKopplungen()])
      .then(([dienste, anrufe, datev]) => {
        if (dienste.status === "fulfilled") {
          setStand(dienste.value); setFehler(null);
        } else {
          // Der vorige Stand bleibt bewusst NICHT stehen: Er wäre ohne Hinweis von einem
          // frischen nicht zu unterscheiden, und ein veralteter Ampelpunkt ist schlimmer
          // als gar keiner.
          setStand(null);
          setFehler("Der Zustand der Schnittstellen konnte nicht abgefragt werden. "
            + "Bitte später noch einmal aktualisieren.");
        }
        if (anrufe.status === "fulfilled") {
          setAircall(anrufe.value); setAircallFehler(false);
        } else {
          setAircall(null); setAircallFehler(true);
        }
        if (datev.status === "fulfilled") {
          setDatevKopplungen(datev.value.items); setDatevFehler(false);
        } else {
          // 403 (nicht ADMIN) fällt hier auf denselben Fall wie jeder andere Fehlschlag:
          // Die Zeile sagt „Nicht abfragbar", nie „Nicht konfiguriert" — sonst behauptete
          // ein fehlendes Recht etwas über den EINRICHTUNGSSTAND der Kopplung.
          setDatevKopplungen(null); setDatevFehler(true);
        }
        setMisst(false);
      });
  }, [interfaceStatus, aircallStatus, listDatevKopplungen]);

  useEffect(() => { if (aktiv) messen(); }, [aktiv, messen]);

  /** Vorgabe für die Unternehmenswahl im DATEV-Detail: das erste sichtbare Unternehmen,
   *  sobald eines feststeht und noch keine Wahl getroffen wurde. */
  useEffect(() => {
    if (datevCompanyId !== null) return;
    const erstes = p.companies?.[0];
    if (erstes !== undefined) setDatevCompanyId(erstes.id);
  }, [p.companies, datevCompanyId]);

  /*
   * Beim VERLASSEN des Bereichs fällt der Stand weg (Muster der Bereiche aus Etappe 1).
   *
   * Eine Messung ist ein Zeitpunkt, kein Bestand: Käme man nach einer halben Stunde in
   * einem anderen Menüpunkt zurück, stünde für einen Augenblick die alte Ampel da — samt
   * eines „Stand", der sie widerlegt. Die Fehlermeldung fällt aus demselben Grund: Sie
   * gehört zu einem Abruf, den man längst verlassen hat.
   */
  useEffect(() => {
    if (aktiv) return;
    setStand(null);
    setFehler(null);
    setAircall(null);
    setAircallFehler(false);
    setDatevKopplungen(null);
    setDatevFehler(false);
  }, [aktiv]);

  /*
   * Der geöffnete Dienst — ABGELEITET beim Zeichnen und nicht in einem zweiten Zustand
   * gehalten. Ein Zustand daneben liefe mit der Adresse auseinander: Nach einer Messung,
   * die den Dienst nicht mehr enthält, stünde rechts eine Zeile, die es nicht mehr gibt.
   */
  const gewaehlt: Dienst | null = dienstSchluessel === null || stand === null
    ? null
    : stand.dienste.find((d) => dienstPfad(d.schluessel) === dienstSchluessel) ?? null;

  /*
   * Die Zeilen der Spalte: die gemessenen Dienste des Servers, danach die Aircall-Zeile.
   *
   * ANGEHÄNGT und nicht einsortiert, obwohl die Hausregel „Schlechtestes oben" lautet:
   * Diese Spalte hat noch nie sortiert — ihre Reihenfolge ist die des Servers, und die
   * bleibt über Messungen hinweg gleich. Eine Liste, in der die Zeilen bei jeder Messung
   * die Plätze tauschen, kostet den Leser mehr, als die Ampel ihm an dieser Stelle gibt
   * (vier Zeilen, jede mit Farbe UND Wort). Wenn hier je zehn Dienste stehen, ist das
   * Sortieren eine eigene, bewusste Änderung — dann für ALLE Zeilen, nicht für diese eine.
   *
   * Die Aircall-Zeile fehlt, solange über sie nichts bekannt ist (erste Messung läuft).
   * Ihr Fehlschlag dagegen ist eine Auskunft und bekommt eine Zeile („Nicht abfragbar").
   */
  const aircallZeile = aircallZustand(aircall, aircallFehler);
  const datevZeile = datevZustand(datevKopplungen, datevFehler);
  const zeilen: SpaltenZeile[] = [
    ...(stand?.dienste ?? []).map((d) => ({
      schluessel: d.schluessel, pfad: dienstPfad(d.schluessel), name: d.name,
      label: ZUSTAND_LABEL[d.zustand], klasse: ZUSTAND_KLASSE[d.zustand],
    })),
    ...(aircall === null && !aircallFehler ? [] : [{
      schluessel: AIRCALL_SCHLUESSEL, pfad: AIRCALL_PFAD, name: AIRCALL_NAME,
      label: aircallZeile.label, klasse: aircallZeile.klasse,
    }]),
    ...(datevKopplungen === null && !datevFehler ? [] : [{
      schluessel: DATEV_SCHLUESSEL, pfad: DATEV_PFAD, name: DATEV_NAME,
      label: datevZeile.label, klasse: datevZeile.klasse,
    }]),
  ];

  const spalte = (
    <div className="schn">
      <div className="schn__kopf">
        <KiKnopf deaktiviert={misst} onClick={messen}>
          Aktualisieren
        </KiKnopf>
        {stand !== null && (
          <span className="schn__zusatz">Stand: {formatZeitpunkt(stand.stand)}</span>
        )}
      </div>

      {/* Fehlerzeile als me-hinweis (ME-Etappe 3, Aufgabe 7) — Rolle `alert` vom Element. */}
      {fehler !== null && <Hinweis art="fehler" className="schn__meldung">{fehler}</Hinweis>}

      {fehler === null && stand === null && (
        <p className="schn__zusatz schn__lade" role="status">Lädt …</p>
      )}

      {/* Die Dienstliste als `me-seitenleiste` mit `me-menueeintrag` (ME-Etappe 4, P17,
          Entscheid 44; vorher eigene Knöpfe mit aria-current, Übergang P6-4). Die Landmarke
          „Dienste“ umfasst seither nur die Liste, nicht mehr Aktualisieren und Stand. */}
      <Navigationsleiste name="Dienste">
        {zeilen.map((z) => (
          <Menueeintrag key={z.schluessel} className="schn__eintrag"
            aktuell={z.pfad === dienstSchluessel}
            /* Der zugängliche Name nennt Dienst UND Zustand: Wer die Seite hört, soll
               die Ampel nicht erst durch Öffnen erfahren. */
            aria-label={`Dienst ${z.name}, ${z.label}, anzeigen`}
            /* Übergang P12-6 (docs/ME-DESIGN.md): Ampelpunkt als eigenes span im Slot
               `icon` — me-ampel kennt nur ok/warnung/fehler (nicht laeuft/aus) und setzt
               ein eigenes aria-label. Der Slot ist aria-hidden: Der Punkt ist reine
               Verstärkung, der Zustand steht daneben im Klartext (eine Ampel ALLEIN wäre
               für die rund acht Prozent Rotgrünblinden keine Auskunft). */
            icon={<span className={`schn__punkt schn__punkt--${z.klasse}`} aria-hidden="true" />}
            /* Auch der offene Dienst meldet: Auf dem iPhone steht die Spalte VOR dem
               Detail (siehe spalteZuerst in App.tsx) — ein Antippen ist dort der Weg
               zurück in den offenen Dienst (dieselbe Regel wie im Untermenü der
               Verwaltung). */
            onGewaehlt={() => p.onOeffnen(z.pfad)}>
            <span className="schn__eintragsinhalt">
              <span className="schn__name">{z.name}</span>
              <Chip className="schn__zustand" statusArt={statusArt(z.klasse)}>
                {z.label}
              </Chip>
            </span>
          </Menueeintrag>
        ))}
      </Navigationsleiste>
    </div>
  );

  /*
   * Das Detail behält die Landmarke „Schnittstellen" aus dem Bestand — anders als bei den
   * Objektbereichen, wo die Detailfläche bewusst keine bekommt: Dort hiesse die zweite
   * Region wie die Spalte daneben und wäre mit ihr verwechselbar. Hier heisst die Spalte
   * „Dienste" und das Detail „Schnittstellen"; beide sind auseinanderzuhalten, und ein
   * Bildschirmleser findet den Bereich weiterhin unter seinem Menünamen.
   */
  const detail = (
    <section className="schn__inhalt" aria-label="Schnittstellen">
      {dienstSchluessel === null ? (
        <p className="schn__leer">
          Links einen Dienst wählen — sein Zustand steht dann hier mit allem, was er über
          sich meldet.
        </p>
      ) : dienstSchluessel === AIRCALL_PFAD ? (
        /*
         * Die Aircall-Zeile steht VOR der Fehlerbehandlung der Dienste-Messung: Sie hängt
         * an einem anderen Weg. Ein Ausfall der Dienste-Messung darf hier nicht „diesen
         * Dienst gibt es nicht" heissen — das wäre dieselbe falsche Behauptung, gegen die
         * die dreistufige Auskunft unten geschrieben wurde, nur mit vertauschten Rollen.
         */
        <AircallDienst
          client={p.client}
          status={aircall}
          statusFehler={aircallFehler}
          onStatusNeu={messen}
          {...(p.companies === undefined ? {} : { companies: p.companies })}
        />
      ) : dienstSchluessel === DATEV_PFAD ? (
        /*
         * Dieselbe Überlegung wie bei Aircall: Die DATEV-Zeile hängt an einem anderen Weg
         * (`listDatevKopplungen`), ein Ausfall der Dienste-Messung darf sie nicht mitreissen.
         * Ohne gewähltes Unternehmen (keine sichtbaren Unternehmen) zeigt `DatevBereich`
         * selbst den Hinweis.
         */
        <DatevBereich
          client={p.client}
          companies={p.companies ?? []}
          companyId={datevCompanyId}
          onUnternehmen={setDatevCompanyId}
        />
      ) : fehler !== null ? (
        /*
         * Ohne Liste gibt es den Dienst nicht zu sehen — aber „diesen Dienst gibt es nicht"
         * darf erst behauptet werden, wenn eine Messung vorliegt. Solange sie läuft, wäre
         * die Aussage schlicht falsch; und wenn sie gescheitert ist, liegt es nicht am
         * Dienst (dieselbe dreistufige Auskunft wie im Artikel-Bereich).
         */
        <p className="schn__leer">
          Der Zustand der Schnittstellen konnte nicht abgefragt werden — ohne ihn lässt sich
          dieser Dienst nicht anzeigen. Bitte in der Spalte noch einmal aktualisieren.
        </p>
      ) : stand === null ? (
        <p className="schn__leer">Lädt …</p>
      ) : gewaehlt === null ? (
        <p className="schn__leer">
          Diesen Dienst gibt es hier nicht — bitte links einen aus der Liste wählen.
        </p>
      ) : (
        <article className="schn__karte">
          <div className="schn__zeile2">
            {/* Übergang P12-6 (docs/ME-DESIGN.md): Ampelpunkt als eigenes span — me-ampel kennt
               nur ok/warnung/fehler (nicht laeuft/aus) und setzt ein eigenes aria-label. */}
            <span className={`schn__punkt schn__punkt--${ZUSTAND_KLASSE[gewaehlt.zustand]}`}
              aria-hidden="true" />
            <h2 className="schn__titel">{gewaehlt.name}</h2>
            <Chip className="schn__zustand"
              statusArt={statusArt(ZUSTAND_KLASSE[gewaehlt.zustand])}>
              {ZUSTAND_LABEL[gewaehlt.zustand]}
            </Chip>
          </div>
          <p className="schn__erklaerung">{gewaehlt.erklaerung}</p>
          {/* Was der Dienst über sich meldet — heute die Belegverträge und
              Gestaltungsprofile der Render-Engine. Ein Dienst, der nichts davon
              kennt, lässt die Zeile weg statt „Verträge: —" zu schreiben. */}
          {(gewaehlt.vertraege.length > 0 || gewaehlt.profile.length > 0) && (
            <p className="schn__zusatz">
              Verträge: {gewaehlt.vertraege.join(", ") || "—"}
              {" · "}
              Profile: {gewaehlt.profile.join(", ") || "—"}
            </p>
          )}
        </article>
      )}
    </section>
  );

  return { spalte, detail };
}
